import { Stack, Text, Group, Box, ScrollArea, HoverCard } from '@mantine/core'
import { IconMessageCircle } from '@tabler/icons-react'
import { useSetAtom } from 'jotai'
import { activeChatCharacterAtom } from '../../atoms/chatAtoms'
import { GlowAvatar } from '../common/GlowAvatar'
import { MarkdownRenderer } from '../common/MarkdownRenderer'
import { PanelTitle } from '../common/PanelTitle'

export function CrewPanel({ characters = [], activeCharacterId, allThreads = [], currentThreadIndex }) {
  const setActiveChat = useSetAtom(activeChatCharacterAtom)

  return (
    <Stack
      gap="xs"
      style={{
        height: '100%',
        background: 'linear-gradient(to top, rgba(6, 14, 26, 0.96), rgba(10, 22, 40, 0.92))',
        borderTop: '1px solid rgba(0, 255, 255, 0.1)',
      }}
      px="lg"
      pt="md"
    >
      <PanelTitle>CREW MANIFEST</PanelTitle>

      <Group gap="sm" wrap="nowrap" mt={4}>
        {characters.map((char) => {
          const isActive = char.id === activeCharacterId
          const description = char.public_description || ''

          return (
            <HoverCard
              key={char.id}
              width={320}
              shadow="lg"
              position="bottom"
              openDelay={300}
              withArrow
            >
              <HoverCard.Target>
                <Box
                  style={{ cursor: 'pointer', position: 'relative' }}
                  onClick={() => setActiveChat(char.id)}
                  className="crew-avatar"
                >
                  <GlowAvatar
                    src={char.avatar_url}
                    name={char.name}
                    size={54}
                    square
                    active={isActive}
                  />
                  {isActive && (
                    <Box
                      style={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#00e5ff',
                        boxShadow: '0 0 6px rgba(0, 229, 255, 0.6)',
                      }}
                    />
                  )}
                  {/* Chat icon — visible on hover */}
                  <Box
                    className="crew-chat-icon"
                    style={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: 'rgba(0, 229, 255, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      boxShadow: '0 0 6px rgba(0, 229, 255, 0.6)',
                    }}
                  >
                    <IconMessageCircle size={12} color="#0a1628" />
                  </Box>
                  <style>{`
                    .crew-avatar:hover .crew-chat-icon { opacity: 1 !important; }
                  `}</style>
                </Box>
              </HoverCard.Target>
              <HoverCard.Dropdown
                style={{
                  background: 'rgba(10, 22, 40, 0.98)',
                  border: '1px solid rgba(0, 255, 255, 0.15)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <Text size="sm" fw={600} c="cyan.3" ff="Orbitron, sans-serif" mb="xs">
                  {char.name}
                </Text>
                {description ? (
                  <MarkdownRenderer
                    content={description}
                    style={{ fontSize: 'calc(0.75rem * var(--bw-content-scale, 1))', color: 'var(--mantine-color-gray-4)', lineHeight: 1.5 }}
                  />
                ) : (
                  <Text size="xs" c="dimmed" fs="italic">
                    No description available
                  </Text>
                )}
              </HoverCard.Dropdown>
            </HoverCard>
          )
        })}
      </Group>

      {allThreads.length > 1 && (
        <>
          <PanelTitle mt="xs">MISSION LOG</PanelTitle>
          <ScrollArea h={60} scrollbarSize={4}>
            <Stack gap={4}>
              {allThreads.map((thread, idx) => (
                <Text
                  key={thread.id}
                  size="xs"
                  c={idx === currentThreadIndex ? 'cyan.4' : idx < currentThreadIndex ? 'green.6' : 'dimmed'}
                  fw={idx === currentThreadIndex ? 600 : 400}
                  lineClamp={1}
                >
                  {idx < currentThreadIndex ? '\u2713 ' : idx === currentThreadIndex ? '\u25B6 ' : '  '}
                  {thread.name}
                </Text>
              ))}
            </Stack>
          </ScrollArea>
        </>
      )}
    </Stack>
  )
}
